<template>
  <BasicHeader :branch="branch" :instance="instance" />

  <section class="bg-gray-50 relative z-0">
    <div class="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
      <h1
        class="mb-7 text-3xl font-normal leading-none tracking-tight text-gray-900 md:text-4xl lg:text-5xl dark:text-white">
        Log in to your account
      </h1>

      <div
        class="w-full bg-white rounded-md shadow-xl dark:border md:mt-0 sm:max-w-xl xl:p-0 dark:bg-gray-800 dark:border-gray-700">
        <div class="p-6 space-y-4 md:space-y-6 sm:p-8">
          <p v-if="success" class="text-sm font-bold text-green-500 mb-4 text-center">
            {{ success }}
          </p>

          <form @submit.prevent="login" class="space-y-4 md:space-y-6">
            <!-- IF no branch is present and no instance yet, prompt for manual Instance URL -->
            <div v-if="instanceLoaded && !instance">
              <label for="manualInstanceUrl" class="block mb-2 text-lg font-medium text-gray-900 dark:text-white">
                Your Domain
              </label>
              <div class="flex items-center bg-gray-50 border border-gray-300 rounded-sm overflow-hidden">
                <input v-model="manualInstanceUrl" type="text" id="manualInstanceUrl" placeholder="Domain"
                  class="w-full bg-transparent border-0 text-gray-900 sm:text-sm px-4 py-4 placeholder-gray-400 focus:ring-0 focus:outline-none"
                  required>
                <span
                  class="bg-gray-100 text-gray-900 font-bold px-4 py-4 text-sm border-l border-gray-300 whitespace-nowrap">
                  .jamlms.co.uk
                </span>
              </div>
            </div>


            <div>
              <label for="email" class="block mb-2 text-lg font-medium capitalize text-gray-900 dark:text-white">
                Your email
              </label>
              <input v-model="email" type="email" name="email" id="email"
                class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-sm focus:ring-primary-600 focus:border-primary-600 block w-full px-10 py-4 placeholder-gray-400"
                placeholder="Email address" required>
            </div>

            <div>
              <label for="password" class="block mb-2 text-lg font-medium text-gray-900 dark:text-white">
                Password
              </label>
              <input v-model="password" type="password" name="password" id="password" placeholder="Password"
                class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-md rounded-sm focus:ring-primary-600 focus:border-primary-600 block w-full py-4 placeholder-gray-400"
                required>
            </div>

            <div class="flex items-center justify-between">
              <a :href="$url('/forgot-password')"
                class="text-sm font-medium text-primary-600 hover:underline dark:text-primary-500">
                Forgot password?
              </a>
            </div>

            <button type="submit" :disabled="loading"
              class="w-full min-h-[2.5rem] text-white !bg-primary-700 hover:!bg-blue-900 focus:ring-4 focus:outline-none focus:ring-primary-300 font-semibold rounded-md text-sm px-5 py-2.5 text-center flex items-center justify-center relative">
              <span v-if="loading" class="absolute flex justify-center items-center uppercase">
                <!-- simple spinner SVG -->
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
              </span>
              <span v-else>LOGIN</span>
            </button>

            <div v-if="error" class="text-sm font-bold text-red-500 space-y-1 whitespace-pre-line">
              {{ error }}
            </div>

          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import BasicHeader from './headers/BasicHeader.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'LoginComponent',
  components: { BasicHeader },

  data() {
    return {
      email: '',
      password: '',
      error: null,
      loading: false,
      manualInstanceUrl: '',
      instanceLoaded: false
    };
  },

  computed: {
    ...mapGetters(['getBranch', 'getInstance']),
    branch() {
      return this.getBranch;       // Vuex‐loaded branch (if any)
    },
    instance() {
      return this.getInstance;     // Vuex‐loaded instance (if any)
    },
    success() {
      return this.$route.query.success || null;
    },
    // Pull branchSlug directly from the route, if present:
    routeBranchSlug() {
      return this.$route.params.branchSlug || null;
    },
    // Derive instanceSlug from Vuex or fallback:
    instanceSlug() {
      return this.getInstance?.domain || '';
    }
  },

  methods: {
    ...mapActions(['fetchBranch', 'fetchInstance']),

    async loadBranchData() {
      try {
        const branchSlug = this.$route.params.branchSlug || null;
        const host = window.location.hostname;
        const instanceSubdomain = host.split('.')[0];

        await this.$store.dispatch('fetchInstance', instanceSubdomain);
        if (branchSlug) {
          await this.$store.dispatch('fetchBranch', branchSlug);
        }
      } finally {
        this.instanceLoaded = true;  // ← NEW
      }
    },


    async login() {
      this.loading = true;
      this.error = null;

      try {
        // 1) Determine instanceUrl & branchUrl
        let instanceUrl = '';
        let branchUrl = this.routeBranchSlug; // now comes straight from the path

        if (this.instance) {
          instanceUrl = this.instance.domain;
        } else if (this.manualInstanceUrl) {
          instanceUrl = this.manualInstanceUrl;
        }

        // 2) Construct GraphQL variables
        const extraArgs = [
          `email: "${this.email}"`,
          `password: "${this.password}"`
        ];
        if (instanceUrl) {
          extraArgs.push(`instance_url: "${instanceUrl}"`);
        }
        if (branchUrl) {
          extraArgs.push(`branch_url: "${branchUrl}"`);
        }

        // 3) Call GraphQL
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
          query: `
            mutation {
              loginUser(${extraArgs.join(', ')}) {
                user { email }
                token
              }
            }
          `
        });

        // 4) Handle validation errors
        if (response.data.errors) {
          const errors = response.data.errors;

          // Collect all validation errors
          const validationMessages = errors.flatMap(err => {
            const validation = err.extensions?.validation;
            if (validation) {
              return Object.values(validation).flat(); // flatten all field messages
            }
            return [err.message];
          });

          // Join all messages into a single string (or use array to list in template)
          this.error = validationMessages.join('\n');
          return;
        }

        // 5) On success, stash token + user
        const { token, user } = response.data.data.loginUser;
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('user', JSON.stringify(user));

        // 6) Redirect to the proper instance domain (or branch)
        const baseDomain = process.env.VUE_APP_BASE_DOMAIN || 'jamlms.co.uk';

        if (branchUrl) {
          // inside a branch, redirect to login-redirect so session logic is centralized
          this.$router.push(`/branch/${branchUrl}/login-redirect?token=${token}`);
        } else {
          // manual instance login: full redirect so middleware sees correct subdomain
          let redirectUrl;
          if (window.location.hostname.includes('localhost')) {
            const port = window.location.port;
            redirectUrl = `http://${instanceUrl}.localhost:${port}/login-redirect?token=${token}`;
          } else {
            redirectUrl = `https://${instanceUrl}.${baseDomain}/login-redirect?token=${token}`;
          }
          window.location.href = redirectUrl;
        }



      }
      catch (e) {
        console.error('Login error:', e);
        this.error = 'An error occurred while logging in. Please try again later.';
      }
      finally {
        this.loading = false;
      }
    }
  },

  created() {
    this.loadBranchData();
  }
};
</script>

<style scoped>
/* (your existing styles) */
</style>
